///
/// EarthBrowser - Volcano Browser
///
/// Modern movable window for browsing volcanoes using local OGC sync data
/// Created by Matt Giger on December 20, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { useCallback, useMemo, useState, useEffect, memo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { sendDatasetAction } from '@/redux/earthBrowserSlice'
import { DatasetBrowserWindow } from '@/components/windows/DatasetBrowserWindow'
import { DatasetVisibilityToggle } from '@/components/windows/DatasetVisibilityToggle'
import { DataBrowserView, type DataBrowserViewHandle } from '@/components/data-browser/DataBrowserView'
import { useRegisterWindowContent } from '@/components/windows/WindowContentRegistry'
import { DataProviderRegistry } from '@/data-providers/DataProviderRegistry'
import { VolcanoDataProvider } from '@/data-providers/VolcanoDataProvider.tsx'
import { VolcanoesTable, type VolcanoProperties } from '@/earthbrowser/dataset-distribution/volcanoes/VolcanoesTable'
import { useDatasetLayerRefresh } from '@/hooks/useDatasetLayerRefresh'
import type { DataRow } from '@/data-providers/types'
import { Mountain } from 'lucide-react'
import { isMobile } from '@/earthbrowser/utils/platform'
import { closeDatasetWindow, type DatasetBrowserWindowState } from '@/redux/dialogSlice'

interface VolcanoBrowserRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
}

export const VolcanoBrowserRenderer = memo(function VolcanoBrowserRenderer({
	windowId,
	windowState,
}: VolcanoBrowserRendererProps) {
	const dispatch = useAppDispatch()
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const volcanoesVisible = visibleDatasets.includes("volcano")

	// Get dataset layer manager from Redux
	const layerManager = useAppSelector(state => state.datasetScene.layerManager)

	// Local state for query parameters and data
	const [volcanoType, setVolcanoType] = useState<string | undefined>(undefined)
	const [isInitialized, setIsInitialized] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [volcanoes, setVolcanoes] = useState<VolcanoProperties[]>([])
	const [types, setTypes] = useState<string[]>([])
	const [totalCount, setTotalCount] = useState(0)
	const [refreshTrigger, setRefreshTrigger] = useState(0)
	const dataBrowserViewRef = useRef<DataBrowserViewHandle>(null)

	// Kept in a ref so the provider's queryFn always reads the current rows
	// instead of the stale closure it was created with
	const entriesRef = useRef<VolcanoProperties[]>([])

	// Load volcano types on mount
	useEffect(() => {
		const loadTypes = async () => {
			try {
				const loadedTypes = await VolcanoesTable.getTypes()
				setTypes(loadedTypes)

				const count = await VolcanoesTable.getCount()
				setTotalCount(count)
			} catch (error) {
				console.error('Failed to load volcano types:', error)
			}
		}

		void loadTypes()
	}, [])

	// Load volcanoes from the local table (mount, filter change, sync refresh)
	const loadVolcanoes = useCallback(async (showLoading: boolean) => {
		if (showLoading) setIsLoading(true)
		try {
			let loadedVolcanoes: VolcanoProperties[]
			if (volcanoType) {
				loadedVolcanoes = await VolcanoesTable.getByType(volcanoType)
			} else {
				loadedVolcanoes = await VolcanoesTable.getAll()
			}
			entriesRef.current = loadedVolcanoes
			setVolcanoes(loadedVolcanoes)
			setRefreshTrigger(prev => prev + 1)
		} catch (error) {
			console.error('Failed to load volcanoes:', error)
		} finally {
			if (showLoading) setIsLoading(false)
		}
	}, [volcanoType])

	// Load volcanoes when type filter changes (and on mount)
	useEffect(() => {
		void loadVolcanoes(true)
	}, [loadVolcanoes])

	// Reload silently whenever the volcanoes layer refreshes after a sync,
	// so the table doesn't show a stale snapshot from when the window opened
	useDatasetLayerRefresh('volcanoes', () => {
		void loadVolcanoes(false)
	})

	// Initialize and update data provider
	useEffect(() => {
		// Don't do anything while loading
		if (isLoading) return

		// Only create provider if not initialized
		if (!isInitialized) {
			// Dispose old provider
			const oldProvider = DataProviderRegistry.get(windowId)
			if (oldProvider) {
				void oldProvider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}

			const provider = new VolcanoDataProvider({
				id: windowId,
				name: 'Volcanoes',
				// Read through the ref so refreshData() always sees current rows
				queryFn: async () => entriesRef.current
			})

			void provider.initialize().then(() => {
				DataProviderRegistry.register(windowId, provider)
				setIsInitialized(true)
			})
		} else {
			// Update existing provider with new data
			const provider = DataProviderRegistry.get(windowId) as VolcanoDataProvider
			if (provider) {
				void provider.refreshData()
			}
		}
	}, [volcanoes, windowId, isLoading, isInitialized])


	// Sync scene layer visibility with Redux state
	useEffect(() => {
		if (layerManager) {
			layerManager.setLayerVisible('volcanoes', volcanoesVisible)
		}
	}, [layerManager, volcanoesVisible])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			const provider = DataProviderRegistry.get(windowId)
			if (provider) {
				void provider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}
		}
	}, [windowId])

	// Handle volcano row click
	const handleRowClick = useCallback((row: DataRow) => {
		const volcano = row as unknown as VolcanoProperties
		const featureId = String(volcano.id)

		// Ensure volcanoes are visible on the map
		if (!volcanoesVisible) {
			dispatch(sendDatasetAction({
				type: "volcano",
				action: "enable"
			}))
			if (layerManager) {
				layerManager.setLayerVisible('volcanoes', true)
			}
		}

		// Directly call layer manager to focus on the volcano
		if (layerManager) {
			layerManager.focusSceneObject('volcanoes', featureId)
		} else {
			console.warn('[VolcanoBrowser] Layer manager not available')
		}

		if (isMobile()) {
			dispatch(closeDatasetWindow(windowId))
		}
	}, [layerManager, volcanoesVisible, dispatch, windowId])

	// Handle refresh
	const handleRefresh = useCallback(() => {
		void loadVolcanoes(true)
	}, [loadVolcanoes])

	// Header actions (filter selectors, refresh button)
	const headerActions = useMemo(() => (
		<div className="flex items-center gap-2 p-1">
			{/* Type Filter */}
			{types.length > 0 && types.length < 10 && (
				<div className="flex items-center gap-1 bg-black/30 rounded-full p-1 pr-2 border border-white/10">
					<select
						value={volcanoType || 'All'}
						onChange={(e) => setVolcanoType(e.target.value === 'All' ? undefined : e.target.value)}
						className="px-3 py-1 text-xs rounded-full bg-transparent text-white/80 border-0 outline-none cursor-pointer hover:text-white"
					>
						<option value="All">All Types</option>
						{types.map((type) => (
							<option key={type} value={type}>{type}</option>
						))}
					</select>
				</div>
			)}
		</div>
	), [types, volcanoType])

	// Content
	const content = useMemo(() => {
		const provider = DataProviderRegistry.get(windowId)

		if (provider && isInitialized && !isLoading) {
			return (
				<DataBrowserView
					ref={dataBrowserViewRef}
					key={`volcano-browser-${refreshTrigger}`}
					provider={provider}
					onRowClick={handleRowClick}
					onRefresh={handleRefresh}
					totalDatabaseCount={totalCount}
					searchPlaceholder="Search volcanoes by name, country, type..."
				/>
			)
		}

		// Loading state
		return (
			<div className="flex flex-col items-center justify-center h-full bg-black/20 backdrop-blur-sm">
				<div className="flex flex-col items-center gap-4 p-8">
					<div className="relative w-16 h-16">
						<div className="absolute inset-0 border-4 border-orange-500/20 rounded-full" />
						<div className="absolute inset-0 border-4 border-transparent border-t-orange-500 rounded-full animate-spin" />
					</div>
					<p className="text-base font-medium text-white">
						Loading Volcanoes
					</p>
					<p className="text-sm text-white/60">
						{isLoading ? 'Fetching data...' : 'Initializing...'}
					</p>
				</div>
			</div>
		)
	}, [windowId, isInitialized, isLoading, handleRowClick, handleRefresh, totalCount, refreshTrigger])

	// Register content with the window content registry for drawer docking
	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	// Handle window being brought to front - focus search input
	const handleBringToFront = useCallback(() => {
		dataBrowserViewRef.current?.focusSearch()
	}, [])

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={<Mountain className="w-5 h-5" />}
			iconColor="text-orange-400"
			headerActions={headerActions}
			titlebarPrefix={<DatasetVisibilityToggle windowType="volcano" />}
			onBringToFront={handleBringToFront}
		>
			{content}
		</DatasetBrowserWindow>
	)
})
